import type { PropsWithChildren } from "react"
import { Link, useLocation } from "react-router-dom"
import { useAuth } from "react-oidc-context"
import { WaveBackground } from "./WaveBackground"
import { useTheme } from "../lib/theme"
import { useT } from "../lib/i18n"

interface AppShellProps {
  fluid?: boolean
}

export function AppShell({ children, fluid = true }: PropsWithChildren<AppShellProps>) {
  const auth = useAuth()
  const { pathname } = useLocation()
  const { theme, toggle } = useTheme()
  const { lang, t, toggle: toggleLang } = useT()
  const isoNow = currentIsoWeek()
  return (
    <>
      <WaveBackground />
      <div style={{
        maxWidth: fluid ? "100%" : 1080,
        margin: "0 auto",
        padding: fluid ? "24px 16px" : "24px",
        position: "relative",
      }}>
        <header style={{ display: "flex", alignItems: "center", gap: 24, marginBottom: 32 }}>
          <Link to="/" style={{ display: "flex", alignItems: "baseline", gap: 8, color: "var(--text)" }}>
            <span style={{ fontSize: 24, fontWeight: 700 }}>
              Eyesight<span style={{ color: "var(--accent)" }}>.</span>
            </span>
            <span style={{ fontSize: 11, color: "var(--muted)", letterSpacing: 1, textTransform: "uppercase" }}>
              {t("by_qsay")}
            </span>
          </Link>
          <nav style={{ display: "flex", gap: 16, marginLeft: 8 }}>
            <TopLink to="/" current={pathname === "/"}>{t("nav_home")}</TopLink>
            <TopLink to="/receipts" current={pathname.startsWith("/receipts")}>{t("nav_receipts")}</TopLink>
            <TopLink to={`/diary/${isoNow}`} current={pathname.startsWith("/diary/") || pathname.startsWith("/forms/")}>{t("nav_diary")}</TopLink>
            <TopLink to="/templates" current={pathname.startsWith("/templates")}>{t("nav_templates")}</TopLink>
            <TopLink to="/settings" current={pathname.startsWith("/settings")}>{t("nav_settings")}</TopLink>
          </nav>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12, color: "var(--muted)", fontSize: 13 }}>
            <button
              onClick={toggleLang}
              aria-label={lang === "en" ? t("switch_lang_it") : t("switch_lang_en")}
              title={lang === "en" ? t("switch_lang_it") : t("switch_lang_en")}
              style={themeBtnStyle}
            >
              {lang === "en" ? "🇬🇧 EN" : "🇮🇹 IT"}
            </button>
            <button
              onClick={toggle}
              aria-label={theme === "dark" ? t("switch_theme_light") : t("switch_theme_dark")}
              title={theme === "dark" ? t("theme_light") : t("theme_dark")}
              style={themeBtnStyle}
            >
              {theme === "dark" ? `☀ ${t("theme_light")}` : `🌙 ${t("theme_dark")}`}
            </button>
            <span>{auth.user?.profile?.email ?? "—"}</span>
            <button onClick={() => void auth.signoutRedirect()} style={btnStyle}>
              {t("signout")}
            </button>
          </div>
        </header>
        {children}
        <footer style={{
          marginTop: 64, paddingTop: 16, paddingBottom: 24,
          borderTop: "1px solid var(--border)",
          display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between",
          color: "var(--muted)", fontSize: 12,
        }}>
          <span>
            {linkifyFooter(t("footer_left"))}
          </span>
          <span>{t("footer_right")}</span>
        </footer>
        {/* The AI assistant is not user-facing — it runs in the background during
            receipt processing to attribute each line to the correct ISTAT cell. */}
      </div>
    </>
  )
}

const btnStyle: React.CSSProperties = {
  background: "transparent",
  color: "var(--accent)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  padding: "6px 12px",
  cursor: "pointer",
}

const themeBtnStyle: React.CSSProperties = {
  background: "transparent",
  color: "var(--muted)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  padding: "6px 10px",
  cursor: "pointer",
  fontSize: 12,
}

function TopLink({ to, current, children }: PropsWithChildren<{ to: string; current: boolean }>) {
  return (
    <Link to={to} style={{
      color: current ? "var(--accent)" : "var(--muted)",
      borderBottom: current ? "2px solid var(--accent)" : "2px solid transparent",
      padding: "4px 2px",
    }}>{children}</Link>
  )
}

function linkifyFooter(s: string): React.ReactNode {
  // Replace "Strategoi Q-Say" → link to q-say.com; remaining "Strategoi" → strategoi.it
  const parts: React.ReactNode[] = []
  let i = 0
  const text = s
  const replacements: Array<{ needle: string; href: string }> = [
    { needle: "Strategoi Q-Say", href: "https://q-say.com" },
    { needle: "Strategoi", href: "https://strategoi.it" },
  ]
  while (i < text.length) {
    let matched = false
    for (const { needle, href } of replacements) {
      if (text.startsWith(needle, i)) {
        parts.push(
          <a key={i} href={href} target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)" }}>
            {needle}
          </a>
        )
        i += needle.length
        matched = true
        break
      }
    }
    if (!matched) {
      // accumulate plain chars until next replacement
      const next = replacements
        .map(r => text.indexOf(r.needle, i))
        .filter(idx => idx >= 0)
        .reduce((min, v) => Math.min(min, v), text.length)
      parts.push(text.slice(i, next))
      i = next
    }
  }
  return parts
}

export function currentIsoWeek(): string {
  const d = new Date()
  const tmp = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
  const dayNum = tmp.getUTCDay() || 7
  tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum)
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1))
  const weekNo = Math.ceil((((tmp.getTime() - yearStart.getTime()) / 86400000) + 1) / 7)
  return `${tmp.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`
}
